/**
 * Structured data component for Google search
 */

import React from "react";

import { useLocation } from "@reach/router";

function StructuredData({ eventDetails, courseDetails, blogDetails }) {
  const { href, origin } = useLocation();

  const siteHref = href ?? "[SEO_CANONICAL_URL]";
  const siteUrl = origin ?? siteHref;

  let structuredDataList = [];

  // Organisation/Logo structured data
  const structuredDataOrganisation = JSON.stringify({
    "@context": "http://schema.org/",
    "@type": "Organization",
    name: "World of Difference",
    url: siteHref,
    logo: `${siteUrl}/wp-content/uploads/sites/10/2018/02/GVI-Logo.png`
  });

  structuredDataList.push(structuredDataOrganisation);

  // Events structured data
  if (eventDetails) {
    const performers = eventDetails?.meetupStaffMembers?.map(
      ({ title, featuredImage }) => ({
        "@type": "Person",
        name: title,
        image: [featuredImage?.node?.sourceUrl || ""]
      })
    );

    structuredDataList.push(
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Event",
        name: "World of Difference Virtual Open Day",
        startDate: eventDetails?.meetupDateTime ?? "",
        endDate: eventDetails?.meetupDateTime ?? "",
        eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        image: [eventDetails?.meetupBackgroundImage ?? ""],
        description: eventDetails?.meetupDescription ?? "",
        location: {
          "@type": "VirtualLocation",
          url: siteHref
        },
        organizer: {
          "@type": "Organization",
          name: eventDetails?.meetupHost ?? "World of Difference",
          url: siteHref
        },
        offers: [
          {
            "@type": "Offer",
            category: "Free"
          }
        ],
        performer: performers
      })
    );
  }

  // Course structured data
  if (courseDetails) {
    structuredDataList.push(
      JSON.stringify({
        ...courseDetails,
        "@context": "https://schema.org",
        "@type": "Course",
        provider: {
          "@type": "Organization",
          name: "World of Difference",
          logo: {
            "@type": "ImageObject",
            url: `${siteUrl}/wp-content/uploads/sites/10/2018/02/GVI-Logo.png`
          }
        }
      })
    );
  }

  // FAQ page structured data
  if (siteUrl.includes("faq")) {
    structuredDataList.push(
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage"
      })
    );
  }

  // Blog structured data
  if (blogDetails) {
    structuredDataList.push(
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": siteHref
        },
        headline: blogDetails?.title,
        image: [blogDetails?.image],
        datePublished: blogDetails?.dateCreated,
        dateModified: blogDetails?.dateModified,
        description: blogDetails?.description,
        author: {
          "@type": "Person",
          name: blogDetails?.author
        },
        publisher: {
          "@type": "Organization",
          name: "World of Difference",
          logo: {
            "@type": "ImageObject",
            url: `${siteUrl}/wp-content/uploads/sites/10/2018/02/GVI-Logo.png`
          }
        },
        speakable: {
          "@type": "SpeakableSpecification",
          xpath: [
            "/html/head/title",
            "/html/head/meta[@name='description']/@content"
          ]
        },
        url: siteHref
      })
    );
  }

  return (
    <>
      {structuredDataList.map((dataItem, idx) => (
        <script
          key={`structure-script-${idx}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: dataItem }}
        />
      ))}
    </>
  );
}

export default StructuredData;
